import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const EditLinkModal = ({ isOpen, onClose, linkId, onSuccess }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  
  const [formData, setFormData] = useState({
    lang: '',
    aurl: '',
    durl: '',
    dend: '',
    td: '',
    status: '',
    html: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);

  // Загружаем данные ссылки при открытии модалки
  useEffect(() => {
    if (isOpen && linkId) {
      fetchLinkData();
    }
  }, [isOpen, linkId]);

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      // Блокируем прокрутку body
      document.body.style.overflow = 'hidden';
    } else {
      // Восстанавливаем прокрутку body
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchLinkData = async () => {
    setInitialLoading(true);
    setError('');
    
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/links/${linkId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Декодируем HTML из Base64 и исправляем кодировку UTF-8
        let decodedHtml = '';
        if (data.link.html) {
          try {
            // Декодируем Base64
            const base64Decoded = atob(data.link.html);
            
            // Исправляем кодировку UTF-8
            const utf8Decoded = decodeURIComponent(escape(base64Decoded));
            
            decodedHtml = utf8Decoded;
          } catch (error) {
            console.error('Ошибка декодирования HTML:', error);
            // Пробуем альтернативный способ декодирования
            try {
              const base64Decoded = atob(data.link.html);
              decodedHtml = base64Decoded;
            } catch (secondError) {
              console.error('Вторая попытка декодирования не удалась:', secondError);
              decodedHtml = data.link.html; // Показываем как есть, если декодирование не удалось
            }
          }
        }

        setFormData({
          lang: data.link.lang || '',
          aurl: data.link.aurl || '',
          durl: data.link.durl || '',
          dat: data.link.dat || '',
          dend: data.link.dend || '',
          td: data.link.td || '',
          status: data.link.status?.toString() || '',
          html: decodedHtml
        });
      } else {
        throw new Error(data.message || 'Ошибка загрузки данных ссылки');
      }
    } catch (error) {
      console.error('Ошибка загрузки данных ссылки:', error);
      setError(error.message);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

             // Подготавливаем данные для отправки (исключаем html, так как оно только для просмотра)
       const { html, ...dataToSend } = formData;
       const updateData = {
         ...dataToSend,
         td: formData.td || '',
         status: formData.status ? parseInt(formData.status) : 0
       };

      const response = await fetch(`${API_URL}${currentLang}/links/${linkId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess();
        onClose();
      } else {
        throw new Error(data.message || 'Ошибка обновления ссылки');
      }
    } catch (error) {
      console.error('Ошибка обновления ссылки:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {currentLang === 'ru' ? 'Редактировать ссылку' : 'Edit Link'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {initialLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Язык' : 'Language'}
                </label>
                <select
                  value={formData.lang}
                  onChange={(e) => handleInputChange('lang', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">{currentLang === 'ru' ? 'Выберите язык' : 'Select language'}</option>
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Статус' : 'Status'}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="0">{currentLang === 'ru' ? 'Ожидает проверки' : 'Pending'}</option>
                  <option value="1">{currentLang === 'ru' ? 'Активная' : 'Active'}</option>
                  <option value="2">{currentLang === 'ru' ? 'Не найдена' : 'Not found'}</option>
                  <option value="99">{currentLang === 'ru' ? 'Снята' : 'Removed'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата создания' : 'Created date'}
                </label>
                <input
                  type="date"
                  value={formData.dat}
                  onChange={(e) => handleInputChange('dat', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата окончания' : 'End date'}
                </label>
                <input
                  type="date"
                  value={formData.dend}
                  onChange={(e) => handleInputChange('dend', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Теги
                </label>
                <input
                  type="text"
                  value={formData.td}
                  onChange={(e) => handleInputChange('td', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="seo, backlink, quality"
                />
              </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'URL акцептора' : 'Acceptor URL'}
              </label>
              <input
                type="url"
                value={formData.aurl}
                onChange={(e) => handleInputChange('aurl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="https://example.com"
                required
              />
            </div>

                         <div className="mt-4">
               <label className="block text-sm font-medium text-gray-700 mb-1">
                 {currentLang === 'ru' ? 'URL донора' : 'Donor URL'}
               </label>
               <input
                 type="url"
                 value={formData.durl}
                 onChange={(e) => handleInputChange('durl', e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                 placeholder="https://donor-site.com"
                 required
               />
             </div>

             <div className="mt-4">
               <label className="block text-sm font-medium text-gray-700 mb-1">
                 HTML код ссылки (только для просмотра)
               </label>
               <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm overflow-x-auto">
                 <pre className="whitespace-pre-wrap break-words">
                   {formData.html || 'HTML код не найден'}
                 </pre>
               </div>
             </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-600 disabled:opacity-50"
              >
                {loading ? (currentLang === 'ru' ? 'Сохранение...' : 'Saving...') : (currentLang === 'ru' ? 'Сохранить' : 'Save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditLinkModal; 